import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
} from '@mui/material';
import { Close as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection } from '../common';

const formatPhoneNumber = (value: string): string => {
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length === 0) return '';
  if (cleaned.length <= 3) return `(${cleaned}`;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

const isValidEmail = (email: string): boolean => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

interface ContactData {
  first: string;
  last: string;
  organization?: string;
  role?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  notes?: string;
  memberIds?: string[];
}

interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ContactData, familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  contact?: { id: string; family_id?: string; data?: { data?: ContactData } } | null;
}

const CONTACT_ROLES = [
  { key: 'doctor', label: 'Doctor' },
  { key: 'dentist', label: 'Dentist' },
  { key: 'teacher', label: 'Teacher' },
  { key: 'coach', label: 'Coach' },
  { key: 'babysitter', label: 'Babysitter' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'school', label: 'School' },
  { key: 'family', label: 'Family' },
  { key: 'friend', label: 'Friend' },
  { key: 'neighbor', label: 'Neighbor' },
  { key: 'emergency', label: 'Emergency Contact' },
  { key: 'other', label: 'Other' },
];

export const ContactFormDialog: React.FC<ContactFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  contact,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!contact;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection(contact?.family_id ? [contact.family_id] : undefined);

  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const [organization, setOrganization] = useState('');
  const [role, setRole] = useState('');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (contact) {
        const data = (contact.data?.data || {}) as ContactData;
        setFirst(data.first || '');
        setLast(data.last || '');
        setOrganization(data.organization || '');
        setRole(data.role || '');
        setEmail(data.email || '');
        setPhone(data.phone ? formatPhoneNumber(data.phone) : '');
        setAddress(data.address || '');
        setCity(data.city || '');
        setState(data.state || '');
        setPostalCode(data.postalCode || '');
        setNotes(data.notes || '');
        setSelectedMembers(data.memberIds || []);
      } else {
        setFirst('');
        setLast('');
        setOrganization('');
        setRole('');
        setEmail('');
        setPhone('');
        setAddress('');
        setCity('');
        setState('');
        setPostalCode('');
        setNotes('');
        setSelectedMembers([]);
      }
      setEmailError(null);
      setError(null);
    }
  }, [open, contact]);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (value && !isValidEmail(value)) {
      setEmailError('Invalid email format');
    } else {
      setEmailError(null);
    }
  };

  const handlePhoneChange = (value: string) => {
    setPhone(formatPhoneNumber(value));
  };

  const handleSave = async () => {
    if (!first.trim()) {
      setError('First name is required');
      return;
    }
    if (email && !isValidEmail(email)) {
      setError('Please enter a valid email address');
      return;
    }
    if (selectedFamilyIds.length === 0) {
      setError('Please select at least one family');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(
        {
          first: first.trim(),
          last: last.trim(),
          organization: organization.trim() || undefined,
          role: role || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          address: address.trim() || undefined,
          city: city.trim() || undefined,
          state: state.trim() || undefined,
          postalCode: postalCode.trim() || undefined,
          notes: notes.trim() || undefined,
          memberIds: selectedMembers.length > 0 ? selectedMembers : undefined,
        },
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save contact');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete contact');
    } finally {
      setSaving(false);
    }
  };

  const toggleMember = (memberId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(memberId)
        ? prev.filter((id) => id !== memberId)
        : [...prev, memberId]
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Contact' : 'New Contact'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="First Name" value={first} onChange={(e) => setFirst(e.target.value)} fullWidth required autoFocus />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Last Name" value={last} onChange={(e) => setLast(e.target.value)} fullWidth />
          </Grid>

          <Grid size={12}>
            <TextField label="Organization" value={organization} onChange={(e) => setOrganization(e.target.value)} fullWidth placeholder="e.g., Pediatrics Associates, Lincoln Elementary" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          <Grid size={12}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select value={role} label="Category" onChange={(e) => setRole(e.target.value)}>
                <MenuItem value=""><em>None</em></MenuItem>
                {CONTACT_ROLES.map((r) => (
                  <MenuItem key={r.key} value={r.key}>{r.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              fullWidth
              type="email"
              error={!!emailError}
              helperText={emailError}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Phone"
              value={phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              fullWidth
              type="tel"
              placeholder="(123) 456-7890"
            />
          </Grid>

          {/* Structured Address */}
          <Grid size={12}>
            <TextField label="Street Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="City" value={city} onChange={(e) => setCity(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="State" value={state} onChange={(e) => setState(e.target.value)} fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <TextField label="ZIP Code" value={postalCode} onChange={(e) => setPostalCode(e.target.value)} fullWidth />
          </Grid>

          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Related Family Members</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {memberList.map((member) => {
                const isSelected = selectedMembers.includes(member.id);
                const name = getMemberName(member.id);
                const color = getMemberColor(member.id);
                return (
                  <Chip
                    key={member.id}
                    avatar={<Avatar sx={{ bgcolor: color }}>{name.charAt(0).toUpperCase()}</Avatar>}
                    label={name}
                    onClick={() => toggleMember(member.id)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{ cursor: 'pointer' }}
                  />
                );
              })}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContactFormDialog;
